import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Grid,
  Typography,
  Paper,
  Box,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  Container,
  FormControlLabel,
  Snackbar,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import HttpClient, { Methods } from "../../../services/HttpClient";
import ProgressIndicator from "../../ProgressIndicator";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import moment from "moment";
import TranslationService from "../../../services/TranslationService";
import PDFJSBackend from "../../backends/pdfjs";

// Icons
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import MailIcon from "@mui/icons-material/Mail";
import RedoIcon from "@mui/icons-material/Redo";

// Media queries
import { useMediaQuery } from "react-responsive";
import { MediaQueries } from "../../MediaQueries";
import { appRoutes } from "../../../constants/AppRoutes";
import { endpoints } from "../../../constants/Endpoints";
import { useRef } from "react";
import Document from "../../Document";
import { fileTypes } from "../../../constants/FileTypes";
import Swal from "sweetalert2";
import PDFViewer from "../../PDFViewer";
import ImageViewer from "../../ImageViewer";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import "moment/locale/nl";

export function RenterDetailView() {
  const isTabletOrMobileDevice = useMediaQuery(MediaQueries.MobileDevice);
  const [isMounted, setMounted] = useState(true);
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const navigate = useNavigate();
  const { id } = useParams();
  const formRef = useRef();

  // State for renewal dialog replacing SweetAlert date prompt
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewDate, setRenewDate] = useState(moment()); // default today

  // State for Mail bestanden dialog
  const [mailDialogOpen, setMailDialogOpen] = useState(false);
  const [mailEmail, setMailEmail] = useState("");
  const [mailLastName, setMailLastName] = useState("");
  const [mailSalutation, setMailSalutation] = useState("meneer");
  const [mailSubmitting, setMailSubmitting] = useState(false);
  const [mailFeedback, setMailFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const textFieldVariant = "outlined";

  const styles = {
    defaultButton: {
      marginLeft: "20px",
      marginBottom: "20px",
      backgroundColor: "#595959",
      color: "#ffffff",
      "&:hover": {
        color: "#d01717",
      },
    },
    saveButton: {
      backgroundColor: "#eb008b",
      marginLeft: "20px",
      marginBottom: "20px",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    mailButton: {
      marginLeft: "20px",
      marginBottom: "20px",
      backgroundColor: "#ec2027",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "yellow !important",
      },
    },
    inactive: {
      color: "#ec2027",
      fontWeight: "bold",
    },
    active: {
      color: "green",
      fontWeight: "bold",
    },
    profilePicture: {
      borderRadius: "50%",
      width: "100%",
      display: "block",
      margin: "0 auto",
      minWidth: "100px",
      maxWidth: "140px",
      textAlign: "center",
    },
    alignRight: {
      textAlign: "right",
    },
  };

  const getData = useCallback(async () => {
    const response = await HttpClient.doRequest(
      Methods.GET,
      endpoints.renter.base + id,
      null
    );
    return await response.json();
  }, [id]);

  useEffect(() => {
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }

      return () => {
        setMounted(false);
      };
    });
  }, [isMounted, getData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    sendData().then((s) => {
      if (s) {
        navigate(appRoutes.shb.renter.overview);
      } else {
        setLoading(false);
      }
    });
  };

  const sendData = async () => {
    const response = await HttpClient.doRequest(
      Methods.PUT,
      endpoints.renter.base + id,
      data
    );
    return response.ok;
  };

  const deregister = async () => {
    const response = await HttpClient.doRequest(
      Methods.DELETE,
      endpoints.renter.base + id,
      null
    );
    if (response.ok) navigate(appRoutes.shb.renter.overview);
  };

  const handleDateChange = (name, date) => {
    if (date != null) {
      var _date = new Date(date);
      var year = _date.getFullYear();
      var month = _date.getMonth();
      var day = _date.getDate();
      var newDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));

      if (isMounted) {
        setData({ ...data, [name]: newDate.toISOString() });
      }
    } else if (isMounted) {
      setData({ ...data, [name]: null });
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (isMounted) {
      setData({ ...data, [name]: checked });
    }
  };

  const handleInputChange = (e) => {
    if (isMounted) {
      const { name, value, type } = e.target;

      setData({
        ...data,
        [name]:
          type === "number" || typeof value === "number"
            ? value !== ""
              ? parseInt(value)
              : 0
            : value.trim(),
      });
    }
  };

  const deleteFileDocument = async (fileType) => {
    const response = await HttpClient.doRequest(
      Methods.DELETE,
      endpoints.document.deleteRenterDocument + id,
      { fileType: fileType }
    );
    if (response.ok) {
      setData({
        ...data,
        fileDocuments: data.fileDocuments.filter(
          (x) => x.fileType !== fileType
        ),
      });
    }
  };

  const sendAccountActivationEmail = async (email) => {
    setLoading(true);

    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.renter.sendAccountActivationEmail +
        "?renterId=" +
        data.id +
        "&email=" +
        email
    );

    const message = await response.json();

    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      Toast.fire({
        icon: message.icon,
        title: message.title,
      });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }
    setLoading(false);
  };

  const downloadFile = (fileType) => {
    HttpClient.doRequest(
      Methods.POST,
      endpoints.document.shbDownloadRenterDocument,
      {
        fileType: fileType,
        renterId: Number.parseInt(id),
      }
    ).then((r) => {
      r.blob().then((b) => setFile(b));
    });
  };

  // "Mail bestanden" dialog handlers (replacement for SweetAlert)
  const openMailFilesDialog = () => {
    setMailEmail(data.applicationUserEmail || "");
    setMailLastName(data.applicationUserLastName || "");
    setMailSalutation("meneer");
    setMailDialogOpen(true);
  };
  const closeMailFilesDialog = () => setMailDialogOpen(false);

  const submitMailFiles = async () => {
    if (mailSubmitting) return;
    // Basic validation
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailEmail);
    if (!mailEmail || !mailLastName || !mailSalutation || !emailValid) {
      setMailFeedback({
        open: true,
        severity: "error",
        message: "Controleer of alle verplichte velden correct zijn ingevuld.",
      });
      return;
    }
    setMailSubmitting(true);
    try {
      const response = await HttpClient.doRequest(
        Methods.POST,
        endpoints.renter.sendFilesToEmail,
        {
          email: mailEmail,
          lastName: mailLastName,
          salutation: mailSalutation,
          renterId: data.id,
        }
      );
      if (response.ok) {
        setMailFeedback({
          open: true,
          severity: "success",
          message: "E-mail is verstuurd.",
        });
        setMailDialogOpen(false);
      } else {
        setMailFeedback({
          open: true,
          severity: "error",
          message:
            "E-mail kon niet worden verstuurd. Mogelijk ontbreken of zijn corrupte bestanden geüpload.",
        });
      }
    } catch (e) {
      setMailFeedback({
        open: true,
        severity: "error",
        message: "Onbekende fout bij versturen e-mail.",
      });
    } finally {
      setMailSubmitting(false);
    }
  };

  const closeMailFeedback = (_, reason) => {
    if (reason === "clickaway") return;
    setMailFeedback({ ...mailFeedback, open: false });
  };

  const renewSubscription = async (date) => {
    setLoading(true);

    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.renter.renewSubscription,
      { renterId: data.id, newDate: date.toISOString() }
    );

    const message = await response.json();

    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      Toast.fire({
        icon: message.icon,
        title: message.title,
      });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }

    var newData = await getData();
    setData(newData);

    setLoading(false);
  };

  // Open renewal dialog (default today)
  const openRenewDialog = () => {
    setRenewDate(moment());
    setRenewOpen(true);
  };
  const closeRenewDialog = () => setRenewOpen(false);
  const submitRenewal = () => {
    if (renewDate && renewDate.isValid()) {
      const utc = new Date(
        Date.UTC(
          renewDate.year(),
          renewDate.month(),
          renewDate.date(),
          0,
          0,
          0,
          0
        )
      );
      renewSubscription(utc).finally(() => setRenewOpen(false));
    } else {
      setRenewOpen(false);
    }
  };

  const [file, setFile] = useState(null);

  return (
    <Fragment>
      {file !== null ? (
        file.type === "application/pdf" ? (
          <PDFViewer
            backend={PDFJSBackend}
            callback={() => setFile(null)}
            src={URL.createObjectURL(file)}
          />
        ) : (
          <ImageViewer
            callback={() => setFile(null)}
            src={URL.createObjectURL(file)}
          />
        )
      ) : null}
      <form
        id="renter-detail-view"
        ref={formRef}
        method="post"
        onSubmit={handleSubmit}
      >
        {isLoading ? (
          <div style={{ width: "100%", textAlign: "center" }}>
            <ProgressIndicator />
          </div>
        ) : (
          <>
            <Container maxWidth={false} disableGutters={true}>
              <Grid container>
                {/* Buttons */}
                <Grid size={{ xs: 12 }} style={styles.alignRight}>
                  <Box pt={3}>
                    <Grid container>
                      <Grid size={{ xs: 12 }}>
                        {!data.applicationUserIsActivated ? (
                          /* Send activation mail */
                          <Button
                            color="error"
                            sx={{ ml: 2.5, mb: 2.5 }}
                            onClick={(_) =>
                              Swal.fire({
                                title: "",
                                text: `Activatiemail versturen naar?`,
                                icon: "info",
                                showCancelButton: true,
                                confirmButtonColor: "#eb008b",
                                confirmButtonText: "Versturen",
                                input: "text",
                                inputPlaceholder: "E-mailadres",
                                inputValue: data.applicationUserEmail,
                                inputValidator: (email) => {
                                  if (!email) {
                                    return "E-mailadres is verplicht";
                                  }
                                  return "";
                                },
                              }).then((result) => {
                                if (result.isConfirmed) {
                                  sendAccountActivationEmail(result.value);
                                }
                              })
                            }
                            variant="contained"
                            startIcon={<MailIcon />}
                          >
                            {isTabletOrMobileDevice ? (
                              <span />
                            ) : (
                              <span>Stuur activatie mail</span>
                            )}
                          </Button>
                        ) : null}

                        {/* Deregistration */}
                        <Button
                          color="neutral"
                          sx={{
                            ml: 2.5,
                            mb: 2.5,
                            backgroundColor: "#595959",
                            color: "#ffffff",
                            "&:hover": { backgroundColor: "#4d4d4d" },
                          }}
                          onClick={(_) => {
                            Swal.fire({
                              title: "Uitschrijven",
                              text: "Weet je zeker dat je de huurder wilt uitschrijven?",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonColor: "#ec2027",
                              confirmButtonText: "Uitschrijven",
                            }).then((result) => {
                              if (result.value) {
                                setLoading(true);
                                deregister();
                              }
                            });
                          }}
                          variant="contained"
                          startIcon={<DeleteIcon />}
                        >
                          {isTabletOrMobileDevice ? (
                            <span />
                          ) : (
                            <span>Uitschrijven</span>
                          )}
                        </Button>

                        {/* Extend registration */}
                        <Button
                          color="neutral"
                          sx={{
                            ml: 2.5,
                            mb: 2.5,
                            backgroundColor: "#595959",
                            color: "#ffffff",
                            "&:hover": { backgroundColor: "#4d4d4d" },
                          }}
                          onClick={openRenewDialog}
                          variant="contained"
                          startIcon={<RedoIcon />}
                        >
                          {isTabletOrMobileDevice ? (
                            <span />
                          ) : (
                            <span>Verleng inschrijving</span>
                          )}
                        </Button>

                        {/* Save */}
                        <Button
                          type="submit"
                          color="primary"
                          sx={{ ml: 2.5, mb: 2.5 }}
                          variant="contained"
                          startIcon={<SaveIcon />}
                          edge="end"
                        >
                          {isTabletOrMobileDevice ? (
                            <span />
                          ) : (
                            <span>Opslaan</span>
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Paper>
                    <Box p={3}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 7 }}>
                          <Typography variant="h4">
                            {data.applicationUserFirstName &&
                            data.applicationUserLastName
                              ? `${data.applicationUserFirstName} ${data.applicationUserLastName}`
                              : ""}
                          </Typography>
                          <Typography gutterBottom paragraph>
                            {data.applicationUserEmail}
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {TranslationService.l.ingeschrevenSinds}
                            {moment(data.registrationDate).format("DD/MM/YYYY")}
                            <br />
                            {TranslationService.l.ingeschrevenTot}
                            {moment(data.paymentExpiresAt).format("DD/MM/YYYY")}
                          </Typography>
                          {/* Removed duplicate Startdatum Huren picker (kept single editable field under Woonwensen) */}
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                          <TextField
                            onChange={handleInputChange}
                            name="shbNotes"
                            multiline
                            minRows={5}
                            variant={textFieldVariant}
                            label="SHB notities"
                            defaultValue={data.shbNotes ? data.shbNotes : ""}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Container>

            <Container maxWidth={false} disableGutters={true}>
              <Grid container spacing={2}>
                {/* Algemene gegevens, instellingen */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Grid container>
                    <Grid size={{ xs: 12 }}>
                      <Paper>
                        <Box p={3}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                  <Typography gutterBottom={false} variant="h6">
                                    {TranslationService.l.algemeneGegevens}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                  <TextField
                                    onChange={handleInputChange}
                                    variant={textFieldVariant}
                                    fullWidth
                                    label={TranslationService.l.bedrijfsnaam}
                                    name="applicationUserCompanyName"
                                    defaultValue={
                                      data.applicationUserCompanyName !== null
                                        ? data.applicationUserCompanyName
                                        : ""
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <TextField
                                    required
                                    onChange={handleInputChange}
                                    variant={textFieldVariant}
                                    fullWidth
                                    label={TranslationService.l.voornaam}
                                    name="applicationUserFirstName"
                                    defaultValue={
                                      data.applicationUserFirstName !== null
                                        ? data.applicationUserFirstName
                                        : ""
                                    }
                                  />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <TextField
                                    required
                                    onChange={handleInputChange}
                                    variant={textFieldVariant}
                                    fullWidth
                                    label={TranslationService.l.achternaam}
                                    name="applicationUserLastName"
                                    defaultValue={
                                      data.applicationUserLastName !== null
                                        ? data.applicationUserLastName
                                        : ""
                                    }
                                  />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <TextField
                                    required
                                    onChange={handleInputChange}
                                    variant={textFieldVariant}
                                    fullWidth
                                    label={TranslationService.l.telefoonnummer}
                                    name="applicationUserPhoneNumber"
                                    defaultValue={
                                      data.applicationUserPhoneNumber !== null
                                        ? data.applicationUserPhoneNumber
                                        : ""
                                    }
                                  />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <TextField
                                    onChange={handleInputChange}
                                    variant={textFieldVariant}
                                    label={
                                      TranslationService.l.nettoInkomenPerMaand
                                    }
                                    name="netIncomePerMonth"
                                    type="number"
                                    inputProps={{ min: "0", step: "1" }}
                                    defaultValue={
                                      data.netIncomePerMonth !== null
                                        ? data.netIncomePerMonth
                                        : 0
                                    }
                                    fullWidth
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      {/* Instellingen */}
                      <Paper>
                        <Box p={3}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="h6">Instellingen</Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <FormControl variant="outlined" fullWidth>
                                <InputLabel>Taal</InputLabel>
                                <Select
                                  label="Interieur"
                                  name="applicationUserLocale"
                                  value={
                                    data.applicationUserLocale !== null
                                      ? data.applicationUserLocale
                                      : "nl_NL"
                                  }
                                  onChange={handleInputChange}
                                  fullWidth
                                >
                                  <MenuItem value="nl_NL">Nederlands</MenuItem>
                                  <MenuItem value="en_US">Engels</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={data.doesReceiveEmails}
                                    onChange={handleCheckboxChange}
                                    name="doesReceiveEmails"
                                    color="primary"
                                  />
                                }
                                label="Geabonneerd op e-mail nieuw woningaanbod"
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Adres, woonwensen */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Grid container>
                    {/* Huidig adres */}
                    <Grid size={{ xs: 12 }}>
                      <Paper>
                        <Box p={3}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="h6">
                                {TranslationService.l.huidigAdres}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                required
                                onChange={handleInputChange}
                                variant={textFieldVariant}
                                label={TranslationService.l.adres}
                                name="street"
                                defaultValue={
                                  data.street !== null ? data.street : ""
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                              <TextField
                                required
                                onChange={handleInputChange}
                                label={TranslationService.l.huisnummer}
                                variant={textFieldVariant}
                                type="number"
                                name="houseNumber"
                                defaultValue={
                                  data.houseNumber !== null
                                    ? data.houseNumber
                                    : ""
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                              <TextField
                                onChange={handleInputChange}
                                label={TranslationService.l.toevoeging}
                                variant={textFieldVariant}
                                name="addition"
                                defaultValue={
                                  data.addition !== null ? data.addition : ""
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                              <TextField
                                required
                                onChange={handleInputChange}
                                variant={textFieldVariant}
                                label={TranslationService.l.postCode}
                                name="zipCode"
                                defaultValue={
                                  data.zipCode !== null ? data.zipCode : ""
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                required
                                onChange={handleInputChange}
                                variant={textFieldVariant}
                                label={TranslationService.l.plaats}
                                name="city"
                                defaultValue={
                                  data.city !== null ? data.city : ""
                                }
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                    {/* Woonwensen */}
                    <Grid size={{ xs: 12 }}>
                      <Paper>
                        <Box p={3}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <Typography variant="h6">
                                    {TranslationService.l.woonwensen}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <FormControl variant="outlined" fullWidth>
                                    <InputLabel>
                                      {TranslationService.l.maximumPrijs}
                                    </InputLabel>
                                    <Select
                                      label={TranslationService.l.maximumPrijs}
                                      type="number"
                                      name="preferredMaxPrice"
                                      value={
                                        data.preferredMaxPrice !== 0
                                          ? data.preferredMaxPrice
                                          : 400
                                      }
                                      onChange={handleInputChange}
                                      fullWidth
                                    >
                                      <MenuItem value={400}>€ 400</MenuItem>
                                      <MenuItem value={500}>€ 500</MenuItem>
                                      <MenuItem value={600}>€ 600</MenuItem>
                                      <MenuItem value={700}>€ 700</MenuItem>
                                      <MenuItem value={800}>€ 800</MenuItem>
                                      <MenuItem value={1000}>€ 1000</MenuItem>
                                      <MenuItem value={2500}>€ 2500</MenuItem>
                                      <MenuItem value={5000}>€ 5000</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <FormControl variant="outlined" fullWidth>
                                    <InputLabel>
                                      {TranslationService.l.interieur}
                                    </InputLabel>
                                    <Select
                                      label={TranslationService.l.interieur}
                                      name="preferredInterior"
                                      value={
                                        data.preferredInterior !== ""
                                          ? data.preferredInterior
                                          : " "
                                      }
                                      onChange={handleInputChange}
                                      fullWidth
                                    >
                                      <MenuItem value=" ">
                                        {TranslationService.l.geenVoorkeur}
                                      </MenuItem>
                                      <MenuItem value="Ongemeubileerd">
                                        {TranslationService.l.ongemeubileerd}
                                      </MenuItem>
                                      <MenuItem value="Gemeubileerd">
                                        {TranslationService.l.gemeubileerd}
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <FormControl variant="outlined" fullWidth>
                                    <InputLabel>
                                      {
                                        TranslationService.l
                                          .minimumAantalSlaapkamers
                                      }
                                    </InputLabel>
                                    <Select
                                      label={
                                        TranslationService.l
                                          .minimumAantalSlaapkamers
                                      }
                                      type="number"
                                      name="preferredMinNumberOfBedrooms"
                                      value={
                                        data.preferredMinNumberOfBedrooms !==
                                        null
                                          ? data.preferredMinNumberOfBedrooms
                                          : 0
                                      }
                                      onChange={handleInputChange}
                                      fullWidth
                                    >
                                      <MenuItem value={0}>
                                        {TranslationService.l.geenVoorkeur}
                                      </MenuItem>
                                      <MenuItem value={-1}>
                                        {
                                          TranslationService.l
                                            .geenAparteSlaapkamer
                                        }
                                      </MenuItem>
                                      <MenuItem value={1}>+1</MenuItem>
                                      <MenuItem value={2}>+2</MenuItem>
                                      <MenuItem value={3}>+3</MenuItem>
                                      <MenuItem value={4}>+4</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <FormControl variant="outlined" fullWidth>
                                    <InputLabel>
                                      {TranslationService.l.stad}
                                    </InputLabel>
                                    <Select
                                      label={TranslationService.l.stad}
                                      name="preferredCity"
                                      value={
                                        data.preferredCity !== ""
                                          ? data.preferredCity
                                          : " "
                                      }
                                      onChange={handleInputChange}
                                      fullWidth
                                    >
                                      <MenuItem value=" ">
                                        {TranslationService.l.geenVoorkeur}
                                      </MenuItem>
                                      <MenuItem value="Breda">Breda</MenuItem>
                                      <MenuItem value="Breda en omgeving">
                                        {TranslationService.l.bredaEnOmgeving}
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                  <LocalizationProvider
                                    dateAdapter={AdapterMoment}
                                    adapterLocale="nl"
                                  >
                                    <DatePicker
                                      label={
                                        TranslationService.l.startdatumHuren
                                      }
                                      value={
                                        data.preferredStartOfRentingDate
                                          ? moment(
                                              data.preferredStartOfRentingDate
                                            )
                                          : null
                                      }
                                      format="DD/MM/YYYY"
                                      onChange={(m) => {
                                        if (m && m.isValid()) {
                                          const utc = new Date(
                                            Date.UTC(
                                              m.year(),
                                              m.month(),
                                              m.date(),
                                              0,
                                              0,
                                              0,
                                              0
                                            )
                                          );
                                          handleDateChange(
                                            "preferredStartOfRentingDate",
                                            utc.toISOString()
                                          );
                                        } else if (!m) {
                                          handleDateChange(
                                            "preferredStartOfRentingDate",
                                            null
                                          );
                                        }
                                      }}
                                      slotProps={{
                                        textField: { fullWidth: true },
                                      }}
                                    />
                                  </LocalizationProvider>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                  <TextField
                                    onChange={handleInputChange}
                                    name="wishes"
                                    multiline={true}
                                    minRows={4}
                                    variant={textFieldVariant}
                                    label={TranslationService.l.overigeWensen}
                                    defaultValue={
                                      data.wishes !== null ? data.wishes : ""
                                    }
                                    fullWidth
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Documenten */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper>
                    <Box p={3}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="h6">Documenten</Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Button
                            onClick={openMailFilesDialog}
                            variant="contained"
                            style={{ ...styles.mailButton, marginLeft: 0 }}
                            startIcon={<MailIcon />}
                          >
                            {isTabletOrMobileDevice ? (
                              <span />
                            ) : (
                              <span>Mail bestanden</span>
                            )}
                          </Button>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          {fileTypes.map((file, index) => {
                            return (
                              <Document
                                key={index}
                                type={index}
                                downloadCallback={downloadFile}
                                deleteCallback={deleteFileDocument}
                                hasBeenUploaded={
                                  data.fileDocuments !== undefined
                                    ? data.fileDocuments.filter(
                                        (x) => x.fileType === index
                                      ).length > 0
                                    : false
                                }
                              />
                            );
                          })}
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          </>
        )}
      </form>
      {/* Renewal dialog with localized MUI DatePicker */}
      <Dialog
        open={renewOpen}
        onClose={closeRenewDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {TranslationService.l.verlengInschrijving || "Verleng inschrijving"}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="nl">
            <DatePicker
              label={
                TranslationService.l.nieuweVervaldatum || "Nieuwe vervaldatum"
              }
              value={renewDate}
              format="DD/MM/YYYY"
              onChange={(m) => {
                if (m && m.isValid()) setRenewDate(m);
              }}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenewDialog}>
            {TranslationService.l.annuleren || "Annuleren"}
          </Button>
          <Button onClick={submitRenewal} variant="contained" color="primary">
            {TranslationService.l.verleng || "Verleng"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Mail bestanden dialog */}
      <Dialog
        open={mailDialogOpen}
        onClose={mailSubmitting ? undefined : closeMailFilesDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mail bestanden</DialogTitle>
        <DialogContent dividers>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <TextField
              required
              label="E-mailadres"
              type="email"
              value={mailEmail}
              onChange={(e) => setMailEmail(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              required
              label="Achternaam"
              value={mailLastName}
              onChange={(e) => setMailLastName(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="mail-salutation-label">Aanhef</InputLabel>
              <Select
                labelId="mail-salutation-label"
                label="Aanhef"
                value={mailSalutation}
                onChange={(e) => setMailSalutation(e.target.value)}
              >
                <MenuItem value="meneer">Meneer</MenuItem>
                <MenuItem value="mevrouw">Mevrouw</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={mailSubmitting} onClick={closeMailFilesDialog}>
            Annuleren
          </Button>
          <Button
            onClick={submitMailFiles}
            disabled={mailSubmitting}
            variant="contained"
            color="primary"
          >
            {mailSubmitting ? "Versturen..." : "Versturen"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={mailFeedback.open}
        autoHideDuration={4000}
        onClose={closeMailFeedback}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeMailFeedback}
          severity={mailFeedback.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {mailFeedback.message}
        </Alert>
      </Snackbar>
      {/* Date picker removed; renewal handled elsewhere or via future replacement */}
    </Fragment>
  );
}
